import { useState } from 'react';
import { Clock, Users, ChevronRight } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { getTodayClasses, getTomorrowClasses, CLASSES, formatShortDate } from '../data/mockData';
import { Skeleton } from './ui/skeleton';

export const ClassWidget = ({ onBookClass, isLoading = false }) => {
  const [activeTab, setActiveTab] = useState('today');
  
  const todayClasses = getTodayClasses();
  const tomorrowClasses = getTomorrowClasses();
  
  // Get this week's classes (next 5 days)
  const weekClasses = CLASSES.slice(0, 20);

  const getStatusBadge = (status, availableSpots, totalSpots) => {
    if (status === 'full') {
      return (
        <span className="status-badge status-full shrink-0 whitespace-nowrap">
          Llena
        </span>
      );
    }
    if (status === 'few') {
      return (
        <span className="status-badge status-few shrink-0 whitespace-nowrap">
          {availableSpots} lugares
        </span>
      );
    }
    return (
      <span className="status-badge status-available shrink-0 whitespace-nowrap">
        {availableSpots}/{totalSpots}
      </span>
    );
  };

  const getLevelBadge = (level) => {
    const labels = {
      beginner: 'Principiante',
      intermediate: 'Intermedio',
      advanced: 'Avanzado',
      all: 'Todos',
    };
    return (
      <span className={`level-badge ${level}`}>
        {labels[level]}
      </span>
    );
  };

  const ClassItem = ({ classData }) => (
    <div
      className="class-item group flex-col items-start justify-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-4"
      onClick={() => onBookClass(classData)}
      data-testid={`class-item-${classData.id}`}
    >
      <div className="flex w-full items-center gap-4 min-w-0 sm:w-auto sm:flex-1">
        {/* Time */}
        <div className="flex flex-col items-center w-14 flex-shrink-0">
          <span className="text-lg font-bold text-slate-900">{classData.time.split(':')[0]}</span>
          <span className="text-xs text-slate-500">:{classData.time.split(':')[1]}</span>
        </div>

        {/* Class Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-slate-900 truncate">{classData.name}</h4>
            {getLevelBadge(classData.level)}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
            <span className="truncate">{classData.coach}</span>
            <span className="flex items-center gap-1 flex-shrink-0">
              <Clock className="w-3 h-3" />
              {classData.duration} min
            </span>
          </div>
        </div>
      </div>

      {/* Status & Action */}
      <div className="flex w-full items-center justify-between gap-3 border-t border-slate-100 pt-3 flex-nowrap overflow-x-auto no-scrollbar sm:w-auto sm:border-0 sm:pt-0 sm:justify-end sm:overflow-visible">
        {getStatusBadge(classData.status, classData.availableSpots, classData.totalSpots)}
        <button
          className="book-btn bg-[#16263B] text-white px-4 py-2 rounded-full text-sm font-bold 
                     shrink-0 whitespace-nowrap
                     hover:shadow-glow transition-all duration-300 flex items-center gap-1"
          onClick={(e) => {
            e.stopPropagation();
            onBookClass(classData);
          }}
          disabled={classData.status === 'full'}
          data-testid={`book-btn-${classData.id}`}
        >
          {classData.status === 'full' ? 'Lista espera' : 'Reservar'}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const ClassList = ({ classes, showDate = false }) => {
    if (isLoading) {
      return (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4">
              <Skeleton className="w-14 h-12 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
              </div>
              <Skeleton className="h-8 w-20 rounded-full" />
            </div>
          ))}
        </div>
      );
    }

    if (classes.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-slate-500">No hay clases programadas</p>
        </div>
      );
    }

    // Group by date if showing week view
    if (showDate) {
      const grouped = classes.reduce((acc, cls) => {
        if (!acc[cls.date]) acc[cls.date] = [];
        acc[cls.date].push(cls);
        return acc;
      }, {});

      return (
        <div className="space-y-6 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
          {Object.entries(grouped).slice(0, 5).map(([date, dateClasses]) => (
            <div key={date}>
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm py-2 mb-2">
                <span className="text-sm font-semibold text-slate-900 capitalize">
                  {formatShortDate(date)}
                </span>
              </div>
              <div className="space-y-1">
                {dateClasses.map((cls) => (
                  <ClassItem key={cls.id} classData={cls} />
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-1 max-h-[350px] overflow-y-auto custom-scrollbar pr-2">
        {classes.map((cls) => (
          <ClassItem key={cls.id} classData={cls} />
        ))}
      </div>
    );
  };

  return (
    <div className="widget-container animate-fade-in" data-testid="class-widget">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex w-full gap-2 overflow-x-auto no-scrollbar bg-slate-100 rounded-xl p-1 mb-4 h-auto">
          <TabsTrigger 
            value="today" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium shrink-0 min-w-[110px] px-4 py-2"
            data-testid="tab-today"
          >
            Hoy
          </TabsTrigger>
          <TabsTrigger 
            value="tomorrow" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium shrink-0 min-w-[110px] px-4 py-2"
            data-testid="tab-tomorrow"
          >
            Mañana
          </TabsTrigger>
          <TabsTrigger 
            value="week" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium shrink-0 min-w-[110px] px-4 py-2"
            data-testid="tab-week"
          >
            Semana
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="mt-0">
          <ClassList classes={todayClasses} />
        </TabsContent>

        <TabsContent value="tomorrow" className="mt-0">
          <ClassList classes={tomorrowClasses} />
        </TabsContent>

        <TabsContent value="week" className="mt-0">
          <ClassList classes={weekClasses} showDate />
        </TabsContent>
      </Tabs>

      {/* Quick stats */}
      <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Users className="w-4 h-4" />
          <span><strong className="text-slate-900">{todayClasses.length}</strong> clases hoy</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Clock className="w-4 h-4" />
          <span><strong className="text-slate-900">6:00</strong> - <strong className="text-slate-900">20:30</strong></span>
        </div>
      </div>
    </div>
  );
};
